import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Shield, LogIn, Loader2, Package, Users, ShoppingCart, BarChart3, ChevronLeft, ChevronRight,
  Search, Download, RefreshCw, Lock, Unlock, CheckCircle, XCircle, LogOut, KeyRound, Settings,
  Trophy, Star, MessageCircle, Copy, Plus, Play, Square, Gift, Eye, Pause, Info, ChevronDown, ChevronUp,
  Send, Phone, MessageSquare, Bot, ArrowUpRight, ArrowDownLeft, Ban, Trash2, UserX, UserCheck, X, AlertTriangle,
} from 'lucide-react';
import AdminMessaging from './AdminMessaging';
import {
  adminLogin, adminGetRaffles, adminCreateRaffle, adminUpdateRaffleStatus, adminToggleFeatured,
  adminGetChests, adminChangeChestState, adminGetUsers, adminGetPurchases,
  adminUpdatePurchaseStatus, adminExportPurchases, adminGetStats, adminChangePassword,
  adminSearchWinner, adminMarkPaid, adminExportWinner, adminSetWinner,
  adminGetPresetConfig, adminGetRaffleDetail,
  adminGetMessages, adminSendSms, adminGetMessagesStats,
  adminBlockUser, adminDeleteUser,
} from '../lib/api';
import { toast } from 'sonner';

// ============ LOGIN ============
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { const res = await adminLogin(email, password); localStorage.setItem('admin_token', res.data.token); onLogin(res.data.token); toast.success('Acceso concedido'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Credenciales incorrectas'); }
    setLoading(false);
  };
  return (
    <div className="min-h-screen bg-[#090A0D] flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-[#0E1016] border border-white/[0.08]">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 rounded-2xl bg-[rgba(255,198,64,0.1)] border border-[rgba(255,198,64,0.25)] flex items-center justify-center mx-auto mb-4"><Shield size={28} className="text-[#FFC640]" /></div>
          <CardTitle className="text-xl font-heading text-[#F7F2E7]">Panel Administrativo</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2"><Label className="text-[#F7F2E7]/80 text-sm">Email</Label><Input value={email} onChange={e=>setEmail(e.target.value)} className="bg-[#121521] border-white/[0.12] text-[#F7F2E7]" placeholder="admin@cofresmagicos.com" /></div>
            <div className="space-y-2"><Label className="text-[#F7F2E7]/80 text-sm">Contraseña</Label><Input type="password" value={password} onChange={e=>setPassword(e.target.value)} className="bg-[#121521] border-white/[0.12] text-[#F7F2E7]" /></div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-[#FFC640] text-[#090A0D] font-heading font-semibold hover:bg-[#FFD36B] rounded-xl">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <><LogIn size={16} className="mr-2" /> Iniciar sesión</>}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ============ DASHBOARD ============
const AdminDashboard = ({ token }) => {
  const [activeTab, setActiveTab] = useState('stats');
  const [raffles, setRaffles] = useState([]);
  const [selectedRaffle, setSelectedRaffle] = useState(null);

  const fetchRaffles = useCallback(async () => {
    try { const res = await adminGetRaffles(token); setRaffles(res.data.raffles || []); if (!selectedRaffle && res.data.raffles?.length > 0) setSelectedRaffle(res.data.raffles[0].raffle_id); }
    catch { }
  }, [token, selectedRaffle]);

  useEffect(() => { fetchRaffles(); }, [fetchRaffles]);

  const handleLogout = () => { localStorage.removeItem('admin_token'); window.location.reload(); };

  const tabs = [
    { id: 'stats', label: 'Dashboard', icon: BarChart3 },
    { id: 'raffles', label: 'Sorteos', icon: Gift },
    { id: 'winner', label: 'Ganador', icon: Trophy },
    { id: 'chests', label: 'Cofres', icon: Package },
    { id: 'users', label: 'Usuarios', icon: Users },
    { id: 'purchases', label: 'Compras', icon: ShoppingCart },
    { id: 'messaging', label: 'Mensajería', icon: MessageCircle },
    { id: 'settings', label: 'Config', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-[#090A0D]">
      <header className="sticky top-0 z-40 bg-[#0E1016] border-b border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3"><Shield size={20} className="text-[#FFC640]" /><span className="font-heading font-bold text-[#F7F2E7]">Admin Panel</span></div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-xs text-[#F7F2E7]/50 hover:text-[#FFC640]">Ver sitio</Link>
            <Button onClick={handleLogout} variant="ghost" size="sm" className="text-[#F7F2E7]/50 hover:text-red-400"><LogOut size={16} /></Button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap
                ${activeTab===t.id ? 'border-[#FFC640] text-[#FFC640]' : 'border-transparent text-[#F7F2E7]/50 hover:text-[#F7F2E7]/80'}`}>
              <t.icon size={14} />{t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Raffle selector for relevant tabs */}
        {['chests','purchases','winner','stats'].includes(activeTab) && raffles.length > 0 && (
          <div className="mb-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[#F7F2E7]/50">Sorteo:</span>
            {raffles.map(r => (
              <button key={r.raffle_id} onClick={() => setSelectedRaffle(r.raffle_id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-heading font-semibold ${selectedRaffle===r.raffle_id ? 'bg-[#FFC640] text-[#090A0D]' : 'bg-[#121521] text-[#F7F2E7]/60 border border-white/[0.08]'}`}>
                {r.name || r.raffle_id} <span className="opacity-50">({r.status})</span>
              </button>
            ))}
          </div>
        )}
        {activeTab === 'stats' && <StatsPanel token={token} raffleId={selectedRaffle} />}
        {activeTab === 'raffles' && <RafflesPanel token={token} onRefresh={fetchRaffles} />}
        {activeTab === 'winner' && <WinnerPanel token={token} raffleId={selectedRaffle} raffles={raffles} />}
        {activeTab === 'chests' && <ChestsPanel token={token} raffleId={selectedRaffle} />}
        {activeTab === 'users' && <UsersPanel token={token} />}
        {activeTab === 'purchases' && <PurchasesPanel token={token} raffleId={selectedRaffle} />}
        {activeTab === 'messaging' && <AdminMessaging token={token} />}
        {activeTab === 'settings' && <SettingsPanel token={token} />}
      </main>
    </div>
  );
};

// ============ STATS ============
const StatsPanel = ({ token, raffleId }) => {
  const [stats, setStats] = useState(null); const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { try { const res = await adminGetStats(token, raffleId); setStats(res.data); } catch {} setLoading(false); })(); }, [token, raffleId]);
  if (loading) return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-[#FFC640]" /></div>;
  const cards = [
    { label: 'Cofres Vendidos', value: stats?.sold||0, sub: `${stats?.percentage||0}%`, color: '#FFC640' },
    { label: 'Reservados', value: stats?.reserved||0, color: '#4BA3FF' },
    { label: 'Disponibles', value: stats?.available||0, color: '#35D07F' },
    { label: 'Gratis', value: `${stats?.free_claimed||0}/${stats?.free_total||0}`, color: '#35D07F' },
    { label: 'Usuarios', value: stats?.total_users||0, color: '#FFB347' },
    { label: 'Compras', value: stats?.total_purchases||0, color: '#D4AF37' },
    { label: 'Ingresos', value: `$${stats?.total_revenue?.toFixed(2)||'0.00'}`, color: '#FFC640' },
    { label: 'Sorteos Activos', value: stats?.active_raffles||0, color: '#4BA3FF' },
  ];
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-heading font-bold text-[#F7F2E7]">Dashboard</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c,i) => (
          <Card key={i} className="bg-[#121521] border border-white/[0.08]"><CardContent className="p-4">
            <p className="text-xs text-[#F7F2E7]/50 mb-1">{c.label}</p>
            <p className="text-xl font-heading font-bold tabular-nums" style={{color:c.color}}>{typeof c.value==='number'?c.value.toLocaleString():c.value}</p>
            {c.sub && <p className="text-xs text-[#F7F2E7]/40 mt-0.5">{c.sub}</p>}
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
};

// ============ RAFFLES MANAGEMENT ============
const PresetCard = ({ type, label, icon, color, bgColor, borderColor, isSelected, onClick, description }) => (
  <button onClick={onClick}
    className={`relative p-5 rounded-2xl border-2 transition-all duration-200 text-left w-full
      ${isSelected ? `${borderColor} ${bgColor} shadow-lg scale-[1.02]` : 'border-white/[0.08] bg-[#121521] hover:border-white/[0.15] hover:bg-[#161A2B]'}`}>
    {isSelected && <div className="absolute top-3 right-3"><CheckCircle size={20} className={color} /></div>}
    <div className={`text-3xl mb-2 ${isSelected ? color : 'text-[#F7F2E7]/60'}`}>{icon}</div>
    <h3 className={`font-heading font-bold text-lg ${isSelected ? color : 'text-[#F7F2E7]'}`}>{label}</h3>
    <p className="text-xs text-[#F7F2E7]/50 mt-1">{description}</p>
  </button>
);

const PrizeTableReadOnly = ({ prizeTable, templateType }) => {
  const levelLabels = { ganador: 'Ganador', nivel_1: 'Nivel 1', nivel_2: 'Nivel 2', nivel_3: 'Nivel 3', nivel_4: 'Nivel 4', nivel_5: 'Nivel 5' };
  if (templateType === 'free') {
    const gratis = prizeTable.gratis || {};
    return (
      <div className="space-y-1">
        <h4 className="text-xs font-heading font-semibold text-[#35D07F] mb-2">Tabla de Premios (GRATIS)</h4>
        <div className="grid grid-cols-2 gap-1">
          {Object.entries(levelLabels).map(([key, lbl]) => (
            <div key={key} className="flex justify-between bg-[#0E1016] rounded px-3 py-1.5">
              <span className="text-xs text-[#F7F2E7]/60">{lbl}</span>
              <span className="text-xs font-heading font-bold text-[#35D07F]">${(gratis[key]||0).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  const basico = prizeTable.basico || {};
  const diamante = prizeTable.diamante || {};
  return (
    <div className="space-y-1">
      <h4 className="text-xs font-heading font-semibold text-[#FFC640] mb-2">Tabla de Premios</h4>
      <div className="grid grid-cols-3 gap-1 text-center mb-1">
        <div className="text-[10px] text-[#F7F2E7]/40"></div>
        <div className="text-[10px] font-heading font-bold text-[#FFC640]">BÁSICO</div>
        <div className="text-[10px] font-heading font-bold text-[#A78BFA]">DIAMANTE</div>
      </div>
      {Object.entries(levelLabels).map(([key, lbl]) => (
        <div key={key} className="grid grid-cols-3 gap-1 text-center">
          <div className="text-xs text-[#F7F2E7]/60 text-left px-2">{lbl}</div>
          <div className="bg-[#0E1016] rounded px-2 py-1 text-xs font-heading font-bold text-[#FFC640]">${(basico[key]||0).toLocaleString()}</div>
          <div className="bg-[#0E1016] rounded px-2 py-1 text-xs font-heading font-bold text-[#A78BFA]">${(diamante[key]||0).toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
};

const RafflesPanel = ({ token, onRefresh }) => {
  const [raffles, setRaffles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [presetConfigs, setPresetConfigs] = useState(null);
  const [viewingRaffle, setViewingRaffle] = useState(null);
  const [raffleDetail, setRaffleDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      const res = await adminGetRaffles(token);
      setRaffles(res.data.raffles || []);
      if (res.data.preset_configs) setPresetConfigs(res.data.preset_configs);
    } catch {}
    setLoading(false);
  }, [token]);
  useEffect(() => { fetchList(); }, [fetchList]);

  const handleSelectPreset = (type) => {
    setSelectedPreset(type);
    const defaultNames = { free: 'Sorteo Gratis', mxn50: 'Sorteo $50', mxn100: 'Sorteo $100' };
    setNewName(defaultNames[type] || '');
  };

  const handleCreate = async () => {
    if (!selectedPreset) { toast.error('Selecciona un preset'); return; }
    if (!newName.trim()) { toast.error('Nombre requerido'); return; }
    setCreating(true);
    try {
      await adminCreateRaffle({ name: newName, template_type: selectedPreset }, token);
      toast.success('Sorteo creado exitosamente');
      setNewName('');
      setSelectedPreset(null);
      fetchList();
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al crear sorteo'); }
    setCreating(false);
  };

  const handleStatusChange = async (rid, status) => {
    try { await adminUpdateRaffleStatus(rid, status, token); toast.success('Estado actualizado'); fetchList(); onRefresh(); }
    catch { toast.error('Error'); }
  };

  const handleToggleFeatured = async (rid) => {
    try { await adminToggleFeatured(rid, token); toast.success('Destacado actualizado'); fetchList(); onRefresh(); }
    catch { toast.error('Error'); }
  };

  const handleViewDetail = async (raffle) => {
    setViewingRaffle(raffle);
    setDetailLoading(true);
    try {
      const res = await adminGetRaffleDetail(raffle.raffle_id, token);
      setRaffleDetail(res.data);
    } catch { toast.error('Error cargando detalle'); }
    setDetailLoading(false);
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/10 text-green-400 border-green-500/20',
      completed: 'bg-[rgba(255,198,64,0.1)] text-[#FFC640] border-[rgba(255,198,64,0.2)]',
      cancelled: 'bg-red-500/10 text-red-400 border-red-500/20',
      draft: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
    };
    return styles[status] || styles.draft;
  };

  const getTypeBadge = (tt) => {
    if (tt === 'free') return { label: 'GRATIS', cls: 'bg-[rgba(53,208,127,0.1)] text-[#35D07F] border-[rgba(53,208,127,0.2)]' };
    if (tt === 'mxn100') return { label: '$100', cls: 'bg-[rgba(75,163,255,0.1)] text-[#4BA3FF] border-[rgba(75,163,255,0.2)]' };
    return { label: '$50', cls: 'bg-[rgba(255,198,64,0.1)] text-[#FFC640] border-[rgba(255,198,64,0.2)]' };
  };

  const activeConfig = selectedPreset && presetConfigs ? presetConfigs[selectedPreset] : null;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-heading font-bold text-[#F7F2E7]">Gestión de Sorteos</h2>

      {/* ========== DETAIL VIEW ========== */}
      {viewingRaffle && (
        <Card className="bg-[#121521] border border-[rgba(255,198,64,0.2)]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-heading text-[#FFC640] flex items-center gap-2">
                <Eye size={16} /> Detalle del Sorteo
              </CardTitle>
              <Button onClick={() => { setViewingRaffle(null); setRaffleDetail(null); }} variant="ghost" size="sm" className="text-[#F7F2E7]/50 hover:text-[#FFC640]">
                <XCircle size={16} className="mr-1" /> Cerrar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {detailLoading ? (
              <div className="flex justify-center py-8"><Loader2 size={24} className="animate-spin text-[#FFC640]" /></div>
            ) : raffleDetail ? (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-heading font-bold text-[#F7F2E7]">{raffleDetail.raffle?.name}</h3>
                  {(() => { const tb = getTypeBadge(raffleDetail.raffle?.template_type); return <span className={`text-[10px] px-2 py-0.5 rounded-full border font-heading font-bold ${tb.cls}`}>{tb.label}</span>; })()}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-heading font-bold ${getStatusBadge(raffleDetail.raffle?.status)}`}>{(raffleDetail.raffle?.status||'').toUpperCase()}</span>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-[#0E1016] rounded-xl p-3 text-center">
                    <p className="text-[10px] text-[#F7F2E7]/40">Vendidos</p>
                    <p className="text-lg font-heading font-bold text-[#FFC640]">{raffleDetail.metrics?.sold || 0}</p>
                    <p className="text-[10px] text-[#F7F2E7]/30">de {raffleDetail.metrics?.total || 0}</p>
                  </div>
                  <div className="bg-[#0E1016] rounded-xl p-3 text-center">
                    <p className="text-[10px] text-[#F7F2E7]/40">Progreso</p>
                    <p className="text-lg font-heading font-bold text-green-400">{raffleDetail.metrics?.percentage || 0}%</p>
                  </div>
                  <div className="bg-[#0E1016] rounded-xl p-3 text-center">
                    <p className="text-[10px] text-[#F7F2E7]/40">Compras</p>
                    <p className="text-lg font-heading font-bold text-[#4BA3FF]">{raffleDetail.metrics?.total_purchases || 0}</p>
                  </div>
                  <div className="bg-[#0E1016] rounded-xl p-3 text-center">
                    <p className="text-[10px] text-[#F7F2E7]/40">Usuarios</p>
                    <p className="text-lg font-heading font-bold text-[#A78BFA]">{raffleDetail.metrics?.total_users || 0}</p>
                  </div>
                </div>

                {/* Preset Config (read-only) */}
                {raffleDetail.preset_config && (
                  <div className="bg-[#0E1016] rounded-xl p-4 space-y-3 border border-white/[0.06]">
                    <h4 className="text-sm font-heading font-semibold text-[#F7F2E7]/80 flex items-center gap-2"><Lock size={14} className="text-[#FFC640]" /> Configuración del Preset (Solo lectura)</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                      <div><span className="text-[#F7F2E7]/40">Total cofres:</span> <span className="font-heading font-bold text-[#F7F2E7] ml-1">{raffleDetail.preset_config.total_chests?.toLocaleString()}</span></div>
                      <div><span className="text-[#F7F2E7]/40">Rango:</span> <span className="font-heading font-bold text-[#F7F2E7] ml-1">{raffleDetail.preset_config.range_label}</span></div>
                      <div><span className="text-[#F7F2E7]/40">Cofres gratis:</span> <span className="font-heading font-bold text-[#F7F2E7] ml-1">{raffleDetail.preset_config.free_chests_count}</span></div>
                      <div className="col-span-2 sm:col-span-3"><span className="text-[#F7F2E7]/40">Participación:</span> <span className="text-[#F7F2E7] ml-1">{raffleDetail.preset_config.participation_rule}</span></div>
                      <div className="col-span-2 sm:col-span-3"><span className="text-[#F7F2E7]/40">Precios:</span> <span className="text-[#F7F2E7] ml-1">{raffleDetail.preset_config.price_rule}</span></div>
                      {raffleDetail.preset_config.estimated_gross > 0 && (
                        <div className="col-span-2 sm:col-span-3"><span className="text-[#F7F2E7]/40">Ingreso bruto estimado:</span> <span className="font-heading font-bold text-green-400 ml-1">${raffleDetail.preset_config.estimated_gross?.toLocaleString()}</span></div>
                      )}
                    </div>
                    <div className="pt-2">
                      {raffleDetail.preset_config.rules?.map((rule, i) => (
                        <div key={i} className="flex items-start gap-2 text-xs text-[#F7F2E7]/50 py-0.5">
                          <Info size={12} className="text-[#FFC640] mt-0.5 shrink-0" />
                          <span>{rule}</span>
                        </div>
                      ))}
                    </div>
                    <PrizeTableReadOnly prizeTable={raffleDetail.preset_config.prize_table} templateType={raffleDetail.preset_config.template_type} />
                  </div>
                )}
              </div>
            ) : <p className="text-[#F7F2E7]/50 text-sm">Error al cargar detalle.</p>}
          </CardContent>
        </Card>
      )}

      {/* ========== CREATE NEW RAFFLE ========== */}
      {!viewingRaffle && (
        <Card className="bg-[#121521] border border-[rgba(255,198,64,0.2)]">
          <CardHeader>
            <CardTitle className="text-base font-heading text-[#FFC640]">
              <Plus size={16} className="inline mr-1" /> Crear Nuevo Sorteo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Preset selector - 3 large cards */}
            <div>
              <Label className="text-[#F7F2E7]/80 text-xs mb-3 block">Selecciona el tipo de sorteo:</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <PresetCard type="free" label="GRATIS" icon="🎁" color="text-[#35D07F]"
                  bgColor="bg-[rgba(53,208,127,0.06)]" borderColor="border-[#35D07F]"
                  isSelected={selectedPreset === 'free'} onClick={() => handleSelectPreset('free')}
                  description="5,000 cofres · 1 por usuario · Sin costo" />
                <PresetCard type="mxn50" label="$50" icon="💰" color="text-[#FFC640]"
                  bgColor="bg-[rgba(255,198,64,0.06)]" borderColor="border-[#FFC640]"
                  isSelected={selectedPreset === 'mxn50'} onClick={() => handleSelectPreset('mxn50')}
                  description="5,000 cofres · $1-$50 · Normal/Diamante" />
                <PresetCard type="mxn100" label="$100" icon="💎" color="text-[#4BA3FF]"
                  bgColor="bg-[rgba(75,163,255,0.06)]" borderColor="border-[#4BA3FF]"
                  isSelected={selectedPreset === 'mxn100'} onClick={() => handleSelectPreset('mxn100')}
                  description="10,000 cofres · $1-$100 · Normal/Diamante" />
              </div>
            </div>

            {/* Config preview (read-only) when preset is selected */}
            {activeConfig && (
              <div className="bg-[#0E1016] rounded-xl p-4 space-y-3 border border-white/[0.06] animate-in fade-in duration-200">
                <h4 className="text-sm font-heading font-semibold text-[#F7F2E7]/80 flex items-center gap-2">
                  <Lock size={14} className="text-[#FFC640]" /> Configuración del sorteo (automática)
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                  <div><span className="text-[#F7F2E7]/40">Total cofres:</span> <span className="font-heading font-bold text-[#F7F2E7] ml-1">{activeConfig.total_chests?.toLocaleString()}</span></div>
                  <div><span className="text-[#F7F2E7]/40">Rango:</span> <span className="font-heading font-bold text-[#F7F2E7] ml-1">{activeConfig.range_label}</span></div>
                  <div><span className="text-[#F7F2E7]/40">Cofres gratis:</span> <span className="font-heading font-bold text-[#F7F2E7] ml-1">{activeConfig.free_chests_count}</span></div>
                  <div className="col-span-2 sm:col-span-3"><span className="text-[#F7F2E7]/40">Participación:</span> <span className="text-[#F7F2E7] ml-1">{activeConfig.participation_rule}</span></div>
                  <div className="col-span-2 sm:col-span-3"><span className="text-[#F7F2E7]/40">Precios:</span> <span className="text-[#F7F2E7] ml-1">{activeConfig.price_rule}</span></div>
                  {activeConfig.estimated_gross > 0 && (
                    <div className="col-span-2 sm:col-span-3"><span className="text-[#F7F2E7]/40">Ingreso bruto estimado:</span> <span className="font-heading font-bold text-green-400 ml-1">${activeConfig.estimated_gross?.toLocaleString()}</span></div>
                  )}
                </div>
                <div className="pt-1">
                  {activeConfig.rules?.map((rule, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs text-[#F7F2E7]/50 py-0.5">
                      <Info size={12} className="text-[#FFC640] mt-0.5 shrink-0" />
                      <span>{rule}</span>
                    </div>
                  ))}
                </div>
                <PrizeTableReadOnly prizeTable={activeConfig.prize_table} templateType={activeConfig.template_type} />
              </div>
            )}

            {/* Editable fields */}
            {selectedPreset && (
              <div className="space-y-3 animate-in fade-in duration-200">
                <div className="space-y-1 max-w-md">
                  <Label className="text-[#F7F2E7]/80 text-xs">Nombre del sorteo (editable)</Label>
                  <Input value={newName} onChange={e => setNewName(e.target.value)}
                    className="bg-[#171B2A] border-white/[0.12] text-[#F7F2E7] h-10"
                    placeholder="Ej: Sorteo Especial Navidad" />
                </div>
                <Button onClick={handleCreate} disabled={creating}
                  className="bg-[#FFC640] text-[#090A0D] font-heading font-semibold hover:bg-[#FFD36B] rounded-xl h-10 px-8 text-sm">
                  {creating ? <Loader2 size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
                  Crear Sorteo
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ========== RAFFLE LIST ========== */}
      {!viewingRaffle && (
        <>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-heading font-semibold text-[#F7F2E7]/60">Sorteos existentes ({raffles.length})</h3>
            <Button onClick={fetchList} variant="ghost" size="sm" className="text-[#F7F2E7]/40 hover:text-[#FFC640]">
              <RefreshCw size={14} />
            </Button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-[#FFC640]" /></div>
          ) : (
            <div className="space-y-3">
              {raffles.map(r => {
                const tb = getTypeBadge(r.template_type);
                return (
                  <Card key={r.raffle_id} className={`bg-[#121521] border ${r.status === 'active' ? 'border-[rgba(255,198,64,0.2)]' : 'border-white/[0.06]'}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-heading font-semibold text-[#F7F2E7] truncate">{r.name || r.raffle_id}</h3>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-heading font-bold ${tb.cls}`}>{tb.label}</span>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-heading font-bold ${getStatusBadge(r.status)}`}>{(r.status || 'draft').toUpperCase()}</span>
                            {r.featured && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FFC640] text-[#090A0D] font-heading font-bold">DESTACADO</span>}
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#F7F2E7]/40">
                            <span>Vendidos: <span className="font-heading font-semibold text-[#F7F2E7]/70">{r.sold}/{r.total_chests}</span> <span className="text-[#FFC640]">({r.percentage}%)</span></span>
                            <span>Compras: <span className="font-heading font-semibold text-[#F7F2E7]/70">{r.total_purchases}</span></span>
                            <span>Usuarios: <span className="font-heading font-semibold text-[#F7F2E7]/70">{r.total_users}</span></span>
                          </div>
                          {/* Progress bar */}
                          <div className="mt-2 h-1.5 bg-white/[0.06] rounded-full overflow-hidden max-w-xs">
                            <div className="h-full bg-gradient-to-r from-[#FFC640] to-[#FFD36B] rounded-full transition-all" style={{ width: `${Math.min(r.percentage, 100)}%` }} />
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleViewDetail(r)} className="p-1.5 rounded hover:bg-[#4BA3FF]/10 text-[#4BA3FF]/60 hover:text-[#4BA3FF]" title="Ver detalle"><Eye size={16} /></button>
                          <button onClick={() => handleToggleFeatured(r.raffle_id)} className={`p-1.5 rounded ${r.featured ? 'bg-[#FFC640]/20 text-[#FFC640]' : 'hover:bg-[#FFC640]/10 text-[#F7F2E7]/30 hover:text-[#FFC640]'}`} title={r.featured ? 'Quitar destacado' : 'Destacar'}><Star size={16} /></button>
                          {r.status === 'active' && <button onClick={() => handleStatusChange(r.raffle_id, 'completed')} className="p-1.5 rounded hover:bg-[#FFC640]/10 text-[#FFC640]/60 hover:text-[#FFC640]" title="Finalizar"><CheckCircle size={16} /></button>}
                          {r.status === 'active' && <button onClick={() => handleStatusChange(r.raffle_id, 'cancelled')} className="p-1.5 rounded hover:bg-red-500/10 text-red-400/60 hover:text-red-400" title="Cancelar"><XCircle size={16} /></button>}
                          {r.status !== 'active' && <button onClick={() => handleStatusChange(r.raffle_id, 'active')} className="p-1.5 rounded hover:bg-green-500/10 text-green-400/60 hover:text-green-400" title="Reactivar"><Play size={16} /></button>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {raffles.length === 0 && <p className="text-[#F7F2E7]/40 text-sm text-center py-6">No hay sorteos creados.</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ============ WINNER ============
const WinnerPanel = ({ token, raffleId, raffles = [] }) => {
  const [searchNum, setSearchNum] = useState(''); const [loading, setLoading] = useState(false); const [data, setData] = useState(null); const [error, setError] = useState('');
  
  // Get the current raffle's total_chests for range validation
  const currentRaffle = raffles.find(r => r.raffle_id === raffleId);
  const maxChest = (currentRaffle?.total_chests || 5000) - 1;
  const rangeLabel = `0000-${String(maxChest).padStart(4, '0')}`;

  const handleSearch = async () => {
    const num = parseInt(searchNum.replace('#',''), 10);
    if (isNaN(num) || num < 0 || num > maxChest) { setError(`Cofre no válido (${rangeLabel})`); return; }
    if (!raffleId) { setError('Selecciona un sorteo primero'); return; }
    setError(''); setLoading(true);
    try { const res = await adminSearchWinner(num, raffleId, token); setData(res.data); } catch(err) { setError(err.response?.data?.detail||'Error'); setData(null); }
    setLoading(false);
  };
  const handleMarkPaid = async (userId, level, paid) => {
    try { await adminMarkPaid({raffle_id:raffleId, chest_number:data.chest.chest_number, user_id:userId, level, paid, notes:''}, token); toast.success(paid?'Pagado':'Desmarcado');
      const res = await adminSearchWinner(data.chest.chest_number, raffleId, token); setData(res.data); } catch { toast.error('Error'); }
  };
  const handleExport = async () => {
    try { const res = await adminExportWinner(data.chest.chest_number, raffleId, token); const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href=url; link.download=`ganador_${String(data.chest.chest_number).padStart(4,'0')}.xlsx`; link.click(); toast.success('Descargado'); } catch { toast.error('Error'); }
  };
  const openWA = (phone,name,amount,level) => { window.open(`https://wa.me/${phone}?text=${encodeURIComponent(`Hola ${name}, ¡felicidades! Has ganado ${amount} en COFRES MÁGICOS (${level}).`)}`,'_blank'); };

  const winnerMode = data?.winner?.max_mode;
  const isWinnerDiamante = winnerMode === 'diamante';
  const isFreeRaffle = data?.is_free_raffle;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-heading font-bold text-[#F7F2E7] flex items-center gap-2"><Trophy size={22} className="text-[#FFC640]" /> Buscar Cofre Ganador</h2>
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-md"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40" />
          <Input value={searchNum} onChange={e=>setSearchNum(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSearch()} className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] pl-10 h-11 text-lg font-heading tabular-nums" placeholder={`Cofre ganador (${rangeLabel})`} />
        </div>
        <Button onClick={handleSearch} disabled={loading} className="h-11 px-6 bg-[#FFC640] text-[#090A0D] font-heading font-semibold hover:bg-[#FFD36B] rounded-xl">
          {loading ? <Loader2 size={16} className="animate-spin" /> : 'Ver red y pagos'}
        </Button>
      </div>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      {data && (
        <div className="space-y-4">
          {/* Chest info */}
          <Card className="bg-[#121521] border border-white/[0.08]"><CardContent className="p-5 flex flex-wrap items-center justify-between gap-4">
            <div><p className="text-xs text-[#F7F2E7]/50">Cofre ganador</p><p className="text-3xl font-heading font-bold text-[#FFC640] tabular-nums">{data.chest?.chest_label}</p>
              <p className="text-sm text-[#F7F2E7]/60 mt-1">Precio: <span className="text-[#FFC640]">${data.chest?.price?.toFixed(2)}</span> · Estado: <span className={data.chest?.state==='sold'?'text-green-400':'text-amber-400'}>{data.chest?.state}</span></p></div>
            {winnerMode && (
              isFreeRaffle ? (
                <div className="px-4 py-2 rounded-xl text-sm font-heading font-bold bg-[rgba(53,208,127,0.1)] text-[#35D07F] border border-[rgba(53,208,127,0.3)]">Ganador: GRATIS</div>
              ) : (
                <div className={`px-4 py-2 rounded-xl text-sm font-heading font-bold ${isWinnerDiamante?'bg-[rgba(75,163,255,0.1)] text-[#4BA3FF] border border-[rgba(75,163,255,0.3)]':'bg-[rgba(255,198,64,0.1)] text-[#FFC640] border border-[rgba(255,198,64,0.3)]'}`}>Ganador: {isWinnerDiamante?'DIAMANTE':'NORMAL'}</div>
              )
            )}
          </CardContent></Card>

          {data.chest?.state !== 'sold' && <Card className="bg-[#121521] border border-amber-500/20"><CardContent className="p-5 text-center text-amber-400">Este cofre no ha sido vendido.</CardContent></Card>}

          {data.winner && <>
            {/* Winner */}
            <Card className="bg-[#121521] border border-[rgba(255,198,64,0.2)]"><CardHeader className="pb-2"><CardTitle className="text-base font-heading text-[#FFC640]"><Trophy size={16} className="inline mr-1" /> Ganador</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div><span className="text-[#F7F2E7]/50">Nombre:</span> <span className="text-[#F7F2E7] ml-1">{data.winner.name}</span></div>
                <div><span className="text-[#F7F2E7]/50">WhatsApp:</span> <span className="text-[#F7F2E7] ml-1">{data.winner.phone}</span></div>
                <div><span className="text-[#F7F2E7]/50">Email:</span> <span className="text-[#F7F2E7] ml-1">{data.winner.email||'-'}</span></div>
                <div><span className="text-[#F7F2E7]/50">Cofres:</span> <span className="text-[#FFC640] font-heading ml-1">{data.winner.total_chests_bought}</span> · <span className="text-[#F7F2E7]/50">Gastado:</span> <span className="text-[#FFC640] font-heading ml-1">${data.winner.total_spent?.toFixed(2)}</span></div>
              </CardContent>
            </Card>

            {/* Totals summary */}
            {data.totals && (
              isFreeRaffle ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Card className="bg-[#121521] border border-[rgba(53,208,127,0.2)]"><CardContent className="p-3 text-center">
                    <p className="text-[10px] text-[#F7F2E7]/50">Ganador</p><p className="text-lg font-heading font-bold text-[#35D07F] tabular-nums">${data.totals.winner?.toLocaleString()}</p>
                  </CardContent></Card>
                  <Card className="bg-[#121521] border border-[rgba(53,208,127,0.15)]"><CardContent className="p-3 text-center">
                    <p className="text-[10px] text-[#F7F2E7]/50">Referidos GRATIS</p><p className="text-lg font-heading font-bold text-[#35D07F] tabular-nums">${data.totals.referidos?.toLocaleString()}</p>
                  </CardContent></Card>
                  <Card className="bg-[rgba(53,208,127,0.05)] border border-[rgba(53,208,127,0.3)]"><CardContent className="p-3 text-center">
                    <p className="text-[10px] text-[#35D07F]">TOTAL GENERAL</p><p className="text-lg font-heading font-bold text-[#35D07F] tabular-nums">${data.totals.grand_total?.toLocaleString()}</p>
                  </CardContent></Card>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <Card className="bg-[#121521] border border-[rgba(255,198,64,0.2)]"><CardContent className="p-3 text-center">
                    <p className="text-[10px] text-[#F7F2E7]/50">Ganador</p><p className="text-lg font-heading font-bold text-[#FFC640] tabular-nums">${data.totals.winner?.toLocaleString()}</p>
                  </CardContent></Card>
                  <Card className="bg-[#121521] border border-white/[0.08]"><CardContent className="p-3 text-center">
                    <p className="text-[10px] text-[#F7F2E7]/50">Referidos NORMAL</p><p className="text-lg font-heading font-bold text-[#FFC640] tabular-nums">${data.totals.normal?.toLocaleString()}</p>
                  </CardContent></Card>
                  <Card className="bg-[#121521] border border-[rgba(75,163,255,0.2)]"><CardContent className="p-3 text-center">
                    <p className="text-[10px] text-[#F7F2E7]/50">Referidos DIAMANTE</p><p className="text-lg font-heading font-bold text-[#4BA3FF] tabular-nums">${data.totals.diamante?.toLocaleString()}</p>
                  </CardContent></Card>
                  <Card className="bg-[rgba(255,198,64,0.05)] border border-[rgba(255,198,64,0.3)]"><CardContent className="p-3 text-center">
                    <p className="text-[10px] text-[#FFC640]">TOTAL GENERAL</p><p className="text-lg font-heading font-bold text-[#FFC640] tabular-nums">${data.totals.grand_total?.toLocaleString()}</p>
                  </CardContent></Card>
                </div>
              )
            )}

            {/* Payment table */}
            <Card className="bg-[#0E1016] border border-white/[0.08]">
              <CardHeader className="pb-2"><div className="flex items-center justify-between">
                <CardTitle className="text-base font-heading text-[#F7F2E7]">{isFreeRaffle ? 'Tabla de Pagos (GRATIS)' : 'Tabla de Pagos (por modo de cada usuario)'}</CardTitle>
                <Button onClick={handleExport} variant="outline" size="sm" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] text-xs"><Download size={12} className="mr-1" /> Excel</Button>
              </div></CardHeader>
              <CardContent><div className="overflow-x-auto"><table className="w-full text-sm">
                <thead><tr className="border-b border-white/[0.08]">
                  <th className="text-left py-2 px-2 text-[#F7F2E7]/50 text-xs">Nivel</th>
                  <th className="text-left py-2 px-2 text-[#F7F2E7]/50 text-xs">Nombre</th>
                  <th className="text-left py-2 px-2 text-[#F7F2E7]/50 text-xs">WhatsApp</th>
                  {!isFreeRaffle && <th className="text-left py-2 px-2 text-[#F7F2E7]/50 text-xs">Modo Usuario</th>}
                  <th className="text-left py-2 px-2 text-[#F7F2E7]/50 text-xs">Monto</th>
                  <th className="text-left py-2 px-2 text-[#F7F2E7]/50 text-xs">Habilitado</th>
                  <th className="text-left py-2 px-2 text-[#F7F2E7]/50 text-xs">Estado</th>
                  <th className="text-right py-2 px-2 text-[#F7F2E7]/50 text-xs">Acciones</th>
                </tr></thead>
                <tbody>
                  {/* Winner row */}
                  <tr className="border-b border-[rgba(255,198,64,0.1)] bg-[rgba(255,198,64,0.03)]">
                    <td className="py-2.5 px-2 font-heading font-bold text-[#FFC640]">Ganador</td>
                    <td className="py-2.5 px-2 text-[#F7F2E7]">{data.winner.name}</td>
                    <td className="py-2.5 px-2 text-[#F7F2E7]/70">{data.winner.phone}</td>
                    {!isFreeRaffle && (
                      <td className="py-2.5 px-2"><span className={`text-[10px] px-2 py-0.5 rounded-full font-heading font-bold ${isWinnerDiamante?'bg-[rgba(75,163,255,0.1)] text-[#4BA3FF]':'bg-[rgba(255,198,64,0.1)] text-[#FFC640]'}`}>{isWinnerDiamante?'DIAMANTE':'NORMAL'}</span></td>
                    )}
                    <td className="py-2.5 px-2 font-heading font-bold text-[#FFC640] tabular-nums">${data.winner.prize_amount?.toLocaleString()}</td>
                    <td className="py-2.5 px-2"><span className="text-green-400 text-xs">SI</span></td>
                    <td className="py-2.5 px-2"><span className="text-[#F7F2E7]/30 text-xs">–</span></td>
                    <td className="py-2.5 px-2 text-right"><button onClick={()=>openWA(data.winner.phone,data.winner.name,`$${data.winner.prize_amount?.toLocaleString()}`,'Ganador')} className="p-1 rounded hover:bg-green-500/10 text-green-400/60"><MessageCircle size={14} /></button></td>
                  </tr>

                  {/* Chain rows */}
                  {data.chain?.map(item => {
                    const isItemDiamante = item.user_mode === 'diamante';
                    const isItemGratis = item.user_mode === 'gratis';
                    return (
                      <tr key={item.level} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                        <td className="py-2.5 px-2 font-heading text-[#F7F2E7]">Nivel {item.level}</td>
                        <td className="py-2.5 px-2 text-[#F7F2E7]">{item.user.name}</td>
                        <td className="py-2.5 px-2 text-[#F7F2E7]/70">{item.user.phone}</td>
                        {!isFreeRaffle && (
                          <td className="py-2.5 px-2">
                            {item.enabled ? (
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-heading font-bold ${isItemDiamante?'bg-[rgba(75,163,255,0.1)] text-[#4BA3FF]':'bg-[rgba(255,198,64,0.1)] text-[#FFC640]'}`}>{isItemDiamante?'DIAMANTE':'NORMAL'}</span>
                            ) : <span className="text-[10px] text-[#7A7F8A]">–</span>}
                          </td>
                        )}
                        <td className="py-2.5 px-2 font-heading font-bold tabular-nums" style={{color: item.enabled?(isFreeRaffle?'#35D07F':isItemDiamante?'#4BA3FF':'#FFC640'):'#7A7F8A'}}>
                          {item.enabled ? `$${item.amount?.toLocaleString()}` : '$0'}
                        </td>
                        <td className="py-2.5 px-2">{item.enabled ? <span className="text-green-400 text-xs">SI</span> : <span className="text-red-400 text-xs">NO</span>}</td>
                        <td className="py-2.5 px-2">{item.paid ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">PAGADO</span> : <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">PENDIENTE</span>}</td>
                        <td className="py-2.5 px-2 text-right"><div className="flex justify-end gap-1">
                          {item.enabled && <button onClick={()=>handleMarkPaid(item.user.user_id, item.level, !item.paid)} className="p-1 rounded hover:bg-green-500/10 text-green-400/60"><CheckCircle size={14} /></button>}
                          <button onClick={()=>openWA(item.user.phone,item.user.name,item.enabled?`$${item.amount?.toLocaleString()}`:'$0',`Nivel ${item.level}`)} className="p-1 rounded hover:bg-green-500/10 text-green-400/60"><MessageCircle size={14} /></button>
                          <button onClick={()=>{navigator.clipboard.writeText(`Nivel ${item.level} | ${item.user.name} | ${item.user.phone} | ${isFreeRaffle?'GRATIS':item.user_mode?.toUpperCase()} | $${item.amount?.toLocaleString()}`); toast.success('Copiado');}} className="p-1 rounded hover:bg-white/5 text-[#F7F2E7]/40"><Copy size={14} /></button>
                        </div></td>
                      </tr>
                    );
                  })}
                  {data.chain?.length===0 && <tr><td colSpan={isFreeRaffle?7:8} className="py-6 text-center text-[#F7F2E7]/40 text-sm">Sin referidos. Solo se paga el premio principal.</td></tr>}
                </tbody>
              </table></div></CardContent>
            </Card>
          </>}
        </div>
      )}
    </div>
  );
};

// ============ CHESTS ============
const ChestsPanel = ({ token, raffleId }) => {
  const [chests, setChests] = useState([]); const [page, setPage] = useState(1); const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false); const [search, setSearch] = useState(''); const [stateFilter, setStateFilter] = useState('');
  const fetchData = useCallback(async () => {
    if (!raffleId) return;
    setLoading(true);
    try { const res = await adminGetChests(raffleId, page, 50, stateFilter, search, token); setChests(res.data.chests); setTotalPages(res.data.total_pages); } catch{}
    setLoading(false);
  }, [page, stateFilter, search, token, raffleId]);
  useEffect(() => { fetchData(); }, [fetchData]);
  const changeState = async (cn, st) => { try { await adminChangeChestState(raffleId, cn, st, token); toast.success(`Cofre #${cn} → ${st}`); fetchData(); } catch { toast.error('Error'); } };

  if (!raffleId) return <p className="text-[#F7F2E7]/50">Selecciona un sorteo</p>;
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-heading font-bold text-[#F7F2E7]">Cofres</h2>
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40" />
          <Input value={search} onChange={e=>{setSearch(e.target.value);setPage(1)}} className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] pl-9 text-sm h-9" placeholder="Buscar por número" /></div>
        <select value={stateFilter} onChange={e=>{setStateFilter(e.target.value);setPage(1)}} className="bg-[#121521] border border-white/[0.12] text-[#F7F2E7] rounded-lg px-3 h-9 text-sm">
          <option value="">Todos</option><option value="available">Disponible</option><option value="reserved">Reservado</option><option value="sold">Vendido</option><option value="free">Gratis</option>
        </select>
      </div>
      {loading ? <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-[#FFC640]" /></div> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-white/[0.08]">
          <th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">#</th><th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Precio</th>
          <th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Estado</th><th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Comprador</th>
          <th className="text-right py-2 px-3 text-[#F7F2E7]/50 text-xs">Acciones</th>
        </tr></thead><tbody>{chests.map(c => (
          <tr key={c.chest_number} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
            <td className="py-2 px-3 font-heading text-[#F7F2E7] tabular-nums">{c.chest_label}</td>
            <td className="py-2 px-3 tabular-nums">{c.is_free||c.price===0?<span className="text-[#35D07F] font-heading font-semibold">GRATIS</span>:<span className="text-[#FFC640] font-heading">${c.price?.toFixed(2)}</span>}</td>
            <td className="py-2 px-3"><span className={`inline-flex px-2 py-0.5 text-[10px] rounded-full ${c.state==='available'?'bg-green-500/10 text-green-400 border border-green-500/20':c.state==='reserved'?'bg-blue-500/10 text-blue-400 border border-blue-500/20':'bg-[#F7F2E7]/5 text-[#F7F2E7]/50 border border-white/10'}`}>{c.state==='available'?'Disponible':c.state==='reserved'?'Reservado':'Vendido'}</span></td>
            <td className="py-2 px-3 text-[#F7F2E7]/60 text-xs">{c.buyer_name||'-'}</td>
            <td className="py-2 px-3 text-right"><div className="flex justify-end gap-1">
              {c.state!=='available' && <button onClick={()=>changeState(c.chest_number,'available')} className="p-1 rounded hover:bg-green-500/10 text-green-400/60"><Unlock size={14} /></button>}
              {c.state!=='sold' && <button onClick={()=>changeState(c.chest_number,'sold')} className="p-1 rounded hover:bg-[#FFC640]/10 text-[#FFC640]/60"><CheckCircle size={14} /></button>}
            </div></td>
          </tr>
        ))}</tbody></table></div>
      )}
      <div className="flex items-center justify-center gap-2">
        <Button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} variant="outline" size="sm" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] h-8"><ChevronLeft size={14} /></Button>
        <span className="text-xs text-[#F7F2E7]/50">{page}/{totalPages}</span>
        <Button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} variant="outline" size="sm" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] h-8"><ChevronRight size={14} /></Button>
      </div>
    </div>
  );
};

// ============ USERS ============
const UsersPanel = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showBlockModal, setShowBlockModal] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminGetUsers(page, 50, token, searchQuery, statusFilter);
      setUsers(res.data.users);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Error al cargar usuarios');
    }
    setLoading(false);
  }, [page, token, searchQuery, statusFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setStatusFilter('');
    setPage(1);
  };

  const handleBlock = async (userId, block) => {
    setActionLoading(true);
    try {
      await adminBlockUser(userId, block, blockReason, token);
      toast.success(block ? 'Usuario bloqueado' : 'Usuario desbloqueado');
      setShowBlockModal(null);
      setBlockReason('');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al actualizar usuario');
    }
    setActionLoading(false);
  };

  const handleDelete = async (userId) => {
    setActionLoading(true);
    try {
      await adminDeleteUser(userId, token);
      toast.success('Usuario eliminado');
      setShowDeleteModal(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar usuario');
    }
    setActionLoading(false);
  };

  const getUserStatus = (user) => {
    if (user.deleted) return { label: 'Eliminado', color: 'bg-gray-500/10 text-gray-400' };
    if (user.is_blocked) return { label: 'Bloqueado', color: 'bg-red-500/10 text-red-400' };
    if (user.verified) return { label: 'Activo', color: 'bg-green-500/10 text-green-400' };
    return { label: 'Sin verificar', color: 'bg-amber-500/10 text-amber-400' };
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-heading font-bold text-[#F7F2E7]">Usuarios</h2>
          <p className="text-xs text-[#F7F2E7]/50">{total} usuarios en total</p>
        </div>
      </div>

      {/* Search & Filters */}
      <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F7F2E7]/40" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por teléfono, ID, código o nombre..."
            className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] pl-9 h-9"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="bg-[#121521] border border-white/[0.12] text-[#F7F2E7] rounded-lg px-3 h-9 text-sm"
        >
          <option value="">Activos</option>
          <option value="all">Todos</option>
          <option value="blocked">Bloqueados</option>
          <option value="deleted">Eliminados</option>
        </select>
        <Button type="submit" variant="outline" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] h-9">
          <Search size={14} className="mr-1" /> Buscar
        </Button>
        {(searchQuery || statusFilter) && (
          <Button type="button" onClick={handleClearSearch} variant="outline" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7]/60 h-9">
            <X size={14} className="mr-1" /> Limpiar
          </Button>
        )}
      </form>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-[#FFC640]" /></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.08]">
                <th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Nombre</th>
                <th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Teléfono</th>
                <th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Código</th>
                <th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Referido por</th>
                <th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Estado</th>
                <th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Registro</th>
                <th className="text-right py-2 px-3 text-[#F7F2E7]/50 text-xs">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const status = getUserStatus(u);
                return (
                  <tr key={i} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                    <td className="py-2 px-3 text-[#F7F2E7]">{u.name || '-'}</td>
                    <td className="py-2 px-3 text-[#F7F2E7]/70 font-mono text-xs">{u.deleted ? 'ELIMINADO' : u.phone}</td>
                    <td className="py-2 px-3 text-[#FFC640] font-heading text-xs">{u.referral_code}</td>
                    <td className="py-2 px-3 text-[#F7F2E7]/50 text-xs">{u.referred_by || '-'}</td>
                    <td className="py-2 px-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${status.color}`}>{status.label}</span>
                    </td>
                    <td className="py-2 px-3 text-[#F7F2E7]/40 text-xs">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('es-MX') : ''}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {!u.deleted && (
                        <div className="flex justify-end gap-1">
                          {u.is_blocked ? (
                            <button
                              onClick={() => setShowBlockModal({ user: u, action: 'unblock' })}
                              className="p-1.5 rounded hover:bg-green-500/10 text-green-400/70 hover:text-green-400"
                              title="Desbloquear"
                            >
                              <UserCheck size={14} />
                            </button>
                          ) : (
                            <button
                              onClick={() => setShowBlockModal({ user: u, action: 'block' })}
                              className="p-1.5 rounded hover:bg-amber-500/10 text-amber-400/70 hover:text-amber-400"
                              title="Bloquear"
                            >
                              <Ban size={14} />
                            </button>
                          )}
                          <button
                            onClick={() => setShowDeleteModal(u)}
                            className="p-1.5 rounded hover:bg-red-500/10 text-red-400/70 hover:text-red-400"
                            title="Eliminar"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-[#F7F2E7]/40">
                    No se encontraron usuarios
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-center gap-2">
        <Button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} variant="outline" size="sm" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] h-8">
          <ChevronLeft size={14} />
        </Button>
        <span className="text-xs text-[#F7F2E7]/50">{page}/{totalPages}</span>
        <Button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} variant="outline" size="sm" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] h-8">
          <ChevronRight size={14} />
        </Button>
      </div>

      {/* Block/Unblock Modal */}
      {showBlockModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#0E1016] border border-white/[0.1] rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${showBlockModal.action === 'block' ? 'bg-amber-500/10' : 'bg-green-500/10'}`}>
                {showBlockModal.action === 'block' ? <Ban size={20} className="text-amber-400" /> : <UserCheck size={20} className="text-green-400" />}
              </div>
              <div>
                <h3 className="font-heading font-bold text-[#F7F2E7]">
                  {showBlockModal.action === 'block' ? 'Bloquear Usuario' : 'Desbloquear Usuario'}
                </h3>
                <p className="text-xs text-[#F7F2E7]/50">{showBlockModal.user.name} - {showBlockModal.user.phone}</p>
              </div>
            </div>
            
            {showBlockModal.action === 'block' && (
              <div className="mb-4">
                <p className="text-sm text-[#F7F2E7]/70 mb-3">
                  El usuario no podrá comprar, apartar cofres ni realizar pagos mientras esté bloqueado.
                </p>
                <Label className="text-xs text-[#F7F2E7]/60 mb-1 block">Motivo (opcional)</Label>
                <Input
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="Ej: Comportamiento sospechoso"
                  className="bg-[#121521] border-white/[0.12] text-[#F7F2E7]"
                />
              </div>
            )}
            
            {showBlockModal.action === 'unblock' && (
              <p className="text-sm text-[#F7F2E7]/70 mb-4">
                El usuario podrá volver a operar normalmente en la plataforma.
              </p>
            )}

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowBlockModal(null); setBlockReason(''); }} className="bg-[#121521] border-white/[0.12] text-[#F7F2E7]">
                Cancelar
              </Button>
              <Button
                onClick={() => handleBlock(showBlockModal.user.user_id, showBlockModal.action === 'block')}
                disabled={actionLoading}
                className={showBlockModal.action === 'block' ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}
              >
                {actionLoading ? <Loader2 size={14} className="animate-spin" /> : (showBlockModal.action === 'block' ? 'Bloquear' : 'Desbloquear')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#0E1016] border border-red-500/20 rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <AlertTriangle size={20} className="text-red-400" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-[#F7F2E7]">Eliminar Usuario</h3>
                <p className="text-xs text-[#F7F2E7]/50">{showDeleteModal.name} - {showDeleteModal.phone}</p>
              </div>
            </div>
            
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4 mb-4">
              <p className="text-sm text-red-300/90">
                <strong>⚠️ ¿Estás seguro?</strong><br />
                Esto desactivará la cuenta permanentemente y la ocultará del sistema. El teléfono será anonimizado. Esta acción no se puede revertir fácilmente.
              </p>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowDeleteModal(null)} className="bg-[#121521] border-white/[0.12] text-[#F7F2E7]">
                Cancelar
              </Button>
              <Button
                onClick={() => handleDelete(showDeleteModal.user_id)}
                disabled={actionLoading}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {actionLoading ? <Loader2 size={14} className="animate-spin" /> : 'Eliminar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ PURCHASES ============
const PurchasesPanel = ({ token, raffleId }) => {
  const [purchases, setPurchases] = useState([]); const [page, setPage] = useState(1); const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false); const [statusFilter, setStatusFilter] = useState('');
  const fetchData = useCallback(async () => { setLoading(true); try { const res = await adminGetPurchases(page, 50, statusFilter, token, raffleId); setPurchases(res.data.purchases); setTotalPages(res.data.total_pages); } catch{} setLoading(false); }, [page, statusFilter, token, raffleId]);
  useEffect(() => { fetchData(); }, [fetchData]);
  const updateStatus = async (pid, st) => { try { await adminUpdatePurchaseStatus(pid, st, token); toast.success('Actualizado'); fetchData(); } catch { toast.error('Error'); } };
  const handleExport = async () => { try { const res = await adminExportPurchases(token, raffleId); const url = window.URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a'); a.href=url; a.download='compras.xlsx'; a.click(); toast.success('Descargado'); } catch { toast.error('Error'); } };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-heading font-bold text-[#F7F2E7]">Compras</h2>
        <div className="flex gap-2">
          <select value={statusFilter} onChange={e=>{setStatusFilter(e.target.value);setPage(1)}} className="bg-[#121521] border border-white/[0.12] text-[#F7F2E7] rounded-lg px-3 h-9 text-sm">
            <option value="">Todos</option><option value="pending_payment">Pendiente</option><option value="paid">Pagado</option><option value="cancelled">Cancelado</option>
          </select>
          <Button onClick={handleExport} variant="outline" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] h-9 text-sm"><Download size={14} className="mr-1" /> Excel</Button>
        </div>
      </div>
      {loading ? <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-[#FFC640]" /></div> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-white/[0.08]">
          <th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Nombre</th><th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Cofres</th>
          <th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Modo</th><th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Total</th>
          <th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Estado</th><th className="text-left py-2 px-3 text-[#F7F2E7]/50 text-xs">Fecha</th>
          <th className="text-right py-2 px-3 text-[#F7F2E7]/50 text-xs">Acciones</th>
        </tr></thead><tbody>{purchases.map((p,i) => (
          <tr key={i} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
            <td className="py-2 px-3 text-[#F7F2E7]">{p.name}</td>
            <td className="py-2 px-3 text-[#F7F2E7]/70 text-xs">{p.chest_numbers?.map(cn=>`#${String(cn).padStart(4,'0')}`).join(', ')}</td>
            <td className="py-2 px-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-heading font-bold ${p.mode==='gratis'?'bg-[rgba(53,208,127,0.1)] text-[#35D07F]':(p.mode==='diamante'||(p.chest_numbers?.length||0)>=3)?'bg-[rgba(75,163,255,0.1)] text-[#4BA3FF]':'bg-[rgba(255,198,64,0.1)] text-[#FFC640]'}`}>{p.mode==='gratis'?'GRATIS':(p.mode==='diamante'||(p.chest_numbers?.length||0)>=3)?'DIAMANTE':'BÁSICO'}</span></td>
            <td className="py-2 px-3 text-[#FFC640] font-heading tabular-nums">${p.total?.toFixed(2)}</td>
            <td className="py-2 px-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${p.status==='pending_payment'?'bg-amber-500/10 text-amber-400':p.status==='paid'?'bg-green-500/10 text-green-400':'bg-red-500/10 text-red-400'}`}>{p.status==='pending_payment'?'Pendiente':p.status==='paid'?'Pagado':'Cancelado'}</span></td>
            <td className="py-2 px-3 text-[#F7F2E7]/40 text-xs">{p.created_at?new Date(p.created_at).toLocaleDateString('es-MX'):''}</td>
            <td className="py-2 px-3 text-right"><div className="flex justify-end gap-1">
              {p.status!=='paid' && <button onClick={()=>updateStatus(p.purchase_id,'paid')} className="p-1 rounded hover:bg-green-500/10 text-green-400/60"><CheckCircle size={14} /></button>}
              {p.status!=='cancelled' && <button onClick={()=>updateStatus(p.purchase_id,'cancelled')} className="p-1 rounded hover:bg-red-500/10 text-red-400/60"><XCircle size={14} /></button>}
            </div></td>
          </tr>
        ))}</tbody></table></div>
      )}
      <div className="flex items-center justify-center gap-2">
        <Button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} variant="outline" size="sm" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] h-8"><ChevronLeft size={14} /></Button>
        <span className="text-xs text-[#F7F2E7]/50">{page}/{totalPages}</span>
        <Button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} variant="outline" size="sm" className="bg-[#121521] border-white/[0.12] text-[#F7F2E7] h-8"><ChevronRight size={14} /></Button>
      </div>
    </div>
  );
};


// ============ MESSAGES (TWILIO SMS BOT) ============
const MessagesPanel = ({ token }) => {
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('');
  const [sendTo, setSendTo] = useState('');
  const [sendBody, setSendBody] = useState('');
  const [sending, setSending] = useState(false);
  const [showCompose, setShowCompose] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL || '';
  const webhookUrl = `${backendUrl}/api/twilio/webhook`;

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const [msgRes, statsRes] = await Promise.all([
        adminGetMessages(page, 30, filter || undefined, token),
        adminGetMessagesStats(token)
      ]);
      setMessages(msgRes.data.messages || []);
      setTotalPages(msgRes.data.total_pages || 1);
      setStats(statsRes.data);
    } catch {}
    setLoading(false);
  }, [token, page, filter]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleSend = async () => {
    if (!sendTo.trim() || !sendBody.trim()) { toast.error('Número y mensaje requeridos'); return; }
    setSending(true);
    try {
      await adminSendSms(sendTo, sendBody, token);
      toast.success('SMS enviado');
      setSendTo(''); setSendBody(''); setShowCompose(false);
      fetchMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar SMS');
    }
    setSending(false);
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success('URL del webhook copiada');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-heading font-bold text-[#F7F2E7] flex items-center gap-2">
          <MessageSquare size={20} className="text-[#4BA3FF]" /> Mensajes SMS / Bot
        </h2>
        <div className="flex gap-2">
          <Button onClick={() => setShowCompose(!showCompose)} variant="outline" size="sm"
            className="bg-[rgba(75,163,255,0.1)] border-[rgba(75,163,255,0.2)] text-[#4BA3FF] text-xs">
            <Send size={12} className="mr-1" /> Enviar SMS
          </Button>
          <Button onClick={fetchMessages} variant="ghost" size="sm" className="text-[#F7F2E7]/40 hover:text-[#FFC640]">
            <RefreshCw size={14} />
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Card className="bg-[#121521] border border-white/[0.06]">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-[#F7F2E7]/40">Total</p>
              <p className="text-lg font-heading font-bold text-[#F7F2E7]">{stats.total}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121521] border border-white/[0.06]">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-[#F7F2E7]/40">Recibidos</p>
              <p className="text-lg font-heading font-bold text-[#35D07F]">{stats.inbound}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121521] border border-white/[0.06]">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-[#F7F2E7]/40">Enviados</p>
              <p className="text-lg font-heading font-bold text-[#4BA3FF]">{stats.outbound}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121521] border border-white/[0.06]">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-[#F7F2E7]/40">Bot</p>
              <p className="text-lg font-heading font-bold text-[#A78BFA]">{stats.bot_replies}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#121521] border border-white/[0.06]">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] text-[#F7F2E7]/40">Fallidos</p>
              <p className="text-lg font-heading font-bold text-red-400">{stats.failed}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Webhook URL */}
      <Card className="bg-[#121521] border border-[rgba(75,163,255,0.2)]">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Bot size={20} className="text-[#4BA3FF] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-heading font-semibold text-[#F7F2E7]">Bot SMS Activo</h3>
              <p className="text-xs text-[#F7F2E7]/50 mt-1">
                Configura este URL como webhook en Twilio → Phone Numbers → tu número → Messaging → "A MESSAGE COMES IN":
              </p>
              <div className="flex items-center gap-2 mt-2 bg-[#0E1016] rounded-lg p-2">
                <code className="text-xs text-[#4BA3FF] flex-1 truncate">{webhookUrl}</code>
                <button onClick={copyWebhook} className="p-1 rounded hover:bg-white/[0.06] text-[#F7F2E7]/40 hover:text-[#FFC640]">
                  <Copy size={14} />
                </button>
              </div>
              <p className="text-[10px] text-[#F7F2E7]/30 mt-1">Método: HTTP POST · El bot responde automáticamente a: HOLA, SORTEOS, PREMIOS, REGLAS, ESTADO, AYUDA</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Compose SMS */}
      {showCompose && (
        <Card className="bg-[#121521] border border-[rgba(255,198,64,0.2)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-heading text-[#FFC640]"><Send size={14} className="inline mr-1" /> Enviar SMS</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-[#F7F2E7]/80 text-xs">Número destino (con código país)</Label>
                <Input value={sendTo} onChange={e => setSendTo(e.target.value)}
                  className="bg-[#171B2A] border-white/[0.12] text-[#F7F2E7] h-9"
                  placeholder="+521XXXXXXXXXX" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-[#F7F2E7]/80 text-xs">Mensaje</Label>
              <textarea value={sendBody} onChange={e => setSendBody(e.target.value)}
                className="w-full bg-[#171B2A] border border-white/[0.12] text-[#F7F2E7] rounded-lg px-3 py-2 text-sm h-24 resize-none"
                placeholder="Escribe tu mensaje..." />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSend} disabled={sending}
                className="bg-[#4BA3FF] text-white font-heading font-semibold hover:bg-[#4BA3FF]/80 rounded-xl h-9 text-xs">
                {sending ? <Loader2 size={14} className="animate-spin mr-1" /> : <Send size={14} className="mr-1" />}
                Enviar
              </Button>
              <Button onClick={() => setShowCompose(false)} variant="ghost" size="sm" className="text-[#F7F2E7]/40 text-xs">Cancelar</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-2">
        {[{ v: '', l: 'Todos' }, { v: 'inbound', l: 'Recibidos' }, { v: 'outbound', l: 'Enviados' }].map(f => (
          <button key={f.v} onClick={() => { setFilter(f.v); setPage(1); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-heading font-semibold ${
              filter === f.v ? 'bg-[#FFC640] text-[#090A0D]' : 'bg-[#121521] text-[#F7F2E7]/50 border border-white/[0.08]'
            }`}>{f.l}</button>
        ))}
      </div>

      {/* Messages list */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-[#FFC640]" /></div>
      ) : (
        <div className="space-y-2">
          {messages.map((msg, i) => (
            <Card key={msg.message_id || i} className={`bg-[#121521] border ${msg.direction === 'inbound' ? 'border-[rgba(53,208,127,0.15)]' : 'border-white/[0.06]'}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                    msg.direction === 'inbound' ? 'bg-[rgba(53,208,127,0.1)]' : msg.is_bot ? 'bg-[rgba(167,139,250,0.1)]' : 'bg-[rgba(75,163,255,0.1)]'
                  }`}>
                    {msg.direction === 'inbound' ? <ArrowDownLeft size={14} className="text-[#35D07F]" /> :
                      msg.is_bot ? <Bot size={14} className="text-[#A78BFA]" /> : <ArrowUpRight size={14} className="text-[#4BA3FF]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-heading font-bold ${
                        msg.direction === 'inbound' ? 'bg-[rgba(53,208,127,0.1)] text-[#35D07F]' : 'bg-[rgba(75,163,255,0.1)] text-[#4BA3FF]'
                      }`}>{msg.direction === 'inbound' ? 'RECIBIDO' : 'ENVIADO'}</span>
                      {msg.is_bot && <span className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(167,139,250,0.1)] text-[#A78BFA] font-heading font-bold">BOT</span>}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        msg.status === 'failed' ? 'text-red-400' : 'text-[#F7F2E7]/30'
                      }`}>{msg.status}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-[#F7F2E7]/40">
                      <Phone size={10} />
                      <span>{msg.direction === 'inbound' ? msg.from_number : msg.to_number}</span>
                      <span>·</span>
                      <span>{new Date(msg.created_at).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <p className="text-sm text-[#F7F2E7]/80 mt-1.5 whitespace-pre-wrap break-words">{msg.body}</p>
                    {msg.error && <p className="text-xs text-red-400 mt-1">Error: {msg.error}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {messages.length === 0 && (
            <div className="text-center py-10">
              <MessageSquare size={32} className="text-[#F7F2E7]/20 mx-auto mb-2" />
              <p className="text-[#F7F2E7]/40 text-sm">No hay mensajes aún</p>
              <p className="text-[#F7F2E7]/30 text-xs mt-1">Los mensajes aparecerán aquí cuando los usuarios envíen SMS al bot</p>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1} variant="ghost" size="sm" className="text-[#F7F2E7]/40"><ChevronLeft size={16} /></Button>
          <span className="text-xs text-[#F7F2E7]/50 py-2">{page}/{totalPages}</span>
          <Button onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages} variant="ghost" size="sm" className="text-[#F7F2E7]/40"><ChevronRight size={16} /></Button>
        </div>
      )}
    </div>
  );
};


// ============ SETTINGS ============
const SettingsPanel = ({ token }) => {
  const [curPw, setCurPw] = useState(''); const [newPw, setNewPw] = useState(''); const [loading, setLoading] = useState(false);
  const handleChange = async (e) => {
    e.preventDefault(); if (!curPw||!newPw) { toast.error('Completa los campos'); return; }
    setLoading(true);
    try { await adminChangePassword(curPw, newPw, token); toast.success('Contraseña actualizada'); setCurPw(''); setNewPw(''); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setLoading(false);
  };
  return (
    <div className="max-w-md space-y-6">
      <h2 className="text-xl font-heading font-bold text-[#F7F2E7]">Configuración</h2>
      <Card className="bg-[#121521] border border-white/[0.08]">
        <CardHeader><CardTitle className="text-base font-heading text-[#F7F2E7]"><KeyRound size={16} className="inline mr-1 text-[#FFC640]" /> Cambiar Contraseña</CardTitle></CardHeader>
        <CardContent><form onSubmit={handleChange} className="space-y-3">
          <div className="space-y-1"><Label className="text-[#F7F2E7]/80 text-xs">Contraseña actual</Label><Input type="password" value={curPw} onChange={e=>setCurPw(e.target.value)} className="bg-[#171B2A] border-white/[0.12] text-[#F7F2E7] h-9" /></div>
          <div className="space-y-1"><Label className="text-[#F7F2E7]/80 text-xs">Nueva contraseña</Label><Input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} className="bg-[#171B2A] border-white/[0.12] text-[#F7F2E7] h-9" /></div>
          <Button type="submit" disabled={loading} className="bg-[#FFC640] text-[#090A0D] font-semibold hover:bg-[#FFD36B] rounded-xl text-sm h-9">{loading ? <Loader2 size={14} className="animate-spin" /> : 'Guardar'}</Button>
        </form></CardContent>
      </Card>
    </div>
  );
};

// ============ MAIN ============
const Admin = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  if (!token) return <AdminLogin onLogin={setToken} />;
  return <AdminDashboard token={token} />;
};

export default Admin;
